// This file be kept in sync with the @packages/biling
// TODO: Deprecate this file in favor of @packages/billing
import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import {
  campaignDaysRemaining,
  Campaigns,
  getCampaignEndDate,
  getCampaignStartDate,
  hasCampaignBeenExtended,
  hasCampaignBeenStarted,
  isCampaignActive,
  isCampaignExpired,
} from '@gonfalon/billing/campaigns';
import { USAGE_WARNING_THRESHOLD } from '@gonfalon/billing/constants';
import {
  canMakeSelfServeChanges,
  getEnterpriseCampaign,
  getLimitPlanUpperLimit,
  getSeatLimit,
  getTrialDaysRemaining,
  hasPlan,
  is2021Plan,
  isExpiredTrialWithNoPlan,
  isInvoiced,
  isSelfServe,
  isTrialExpired,
} from '@gonfalon/billing/subscriptions';
import type { LimitDetail, PlanLimitsType } from '@gonfalon/billing/types';
import {
  BillingIntervals,
  BillingProcess,
  LimitNames,
  MarketplaceProviders,
  PlanTypes,
  SubscriptionStates,
  SubscriptionTypes,
} from '@gonfalon/billing/types';
import { getLimitDisplayName } from '@gonfalon/billing/utils';
import {
  enableDeveloperFacade,
  enableDeveloperPlanCheckout,
  enableSelfServeDeveloper2025PlanCheckout,
  enableSelfServeExperimentationKeysLimit,
  enableSelfServePaygFreeUnits,
  enableSelfServeUBBWithAnnualCommits,
  enableUsageExceededSelectiveReadOnly,
  is2018ProTo2021PlanTransitionEnabled,
  switchBillingYearlyToMonthlyGracePeriod,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { formatCurrency } from '@gonfalon/format/formatCurrency';
import { formatNumber } from '@gonfalon/format/formatNumber';
// eslint-disable-next-line no-restricted-imports
import { fromJS, List, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { valueExists } from '@gonfalon/legacy-immutable/immutableUtils';
import type { AnnualCommitOverageProductDetails } from '@gonfalon/plan-manager/types';
import { isFreeDevPlan as checkIsFreeDevPlan } from '@gonfalon/plan-manager/utils';
import { isNil } from 'lodash';

import { objectKeys } from '#utils/collectionUtils';
import type { Link } from '#utils/linkUtils';
import { titlecase } from '#utils/stringUtils';

export const MAX_PROJECT_LIMIT = 2;
export const MAX_ENVIRONMENT_LIMIT = 4;

export const getLimitNameForOverageType = (value: string): LimitNames | undefined =>
  // Check if the value is a valid LimitNames enum value
  Object.values(LimitNames).includes(value as LimitNames) ? (value as LimitNames) : undefined;

type LegacyPlanLimitsType = {
  multipleProjects: boolean;
  multipleEnvironments: boolean;
  abTesting: boolean;
  teams: boolean;
  customRoles: boolean;
  auditLog: boolean;
  mauLimit: number;
};

class LegacyPlanLimits extends Record<LegacyPlanLimitsType>({
  multipleProjects: false,
  multipleEnvironments: false,
  abTesting: false,
  teams: false,
  customRoles: false,
  auditLog: false,
  mauLimit: 0,
}) {
  getMauLimit() {
    return this.mauLimit;
  }

  getProjectLimit() {
    return this.multipleProjects ? -1 : MAX_PROJECT_LIMIT;
  }

  getEnvironmentLimit() {
    return this.multipleEnvironments ? -1 : MAX_ENVIRONMENT_LIMIT;
  }
}

export class PlanLimits extends Record<PlanLimitsType>(
  Object.fromEntries(Object.values(LimitNames).map((name) => [name, null])) as PlanLimitsType,
) {}

type BasePriceType = {
  monthly: number;
  yearly: number;
};

class BasePrice extends Record<BasePriceType>({
  monthly: 0,
  yearly: 0,
}) {
  getMonthlyPrice() {
    return this.monthly;
  }
  getYearlyPrice() {
    return this.yearly;
  }
}

export type LimitTierType = { limit: number };

export class LimitTier extends Record<LimitTierType>({ limit: 0 }) {}

export type LimitPlanType = {
  _links: ImmutableMap<{ self: Link }>;
  name: string;
  version: number;
  tiers: List<LimitTier>;
  included?: number;
};

export class LimitPlan extends Record<LimitPlanType>({
  _links: Map(),
  name: '',
  version: 0,
  tiers: List(),
  included: 0,
}) {
  getLowerLimit() {
    return this.tiers.first(new LimitTier()).limit;
  }

  getUpperLimit() {
    return getLimitPlanUpperLimit({ tiers: this.tiers.toJS() });
  }

  getIncluded(): number {
    return this.included || 0;
  }

  subtractIncludedFromTotal(totalVal: number = 0, included: number = 0) {
    return Math.max(0, totalVal - included); // Ensure we don't return a negative number
  }

  getFixedLimit() {
    return this.getLowerLimit();
  }
}

export type LimitPlansType = {
  [K in LimitNames]: LimitPlan;
};

export class LimitPlans extends Record<LimitPlansType>(
  Object.fromEntries(Object.values(LimitNames).map((name) => [name, createLimitPlan()])) as LimitPlansType,
) {}

export const getStarterPlanMauLimit = (activePlan: Plan, monthlyActiveUsersLimitPlan: LimitPlan) =>
  // 2018 starter plans get their fixed limit from a flag
  activePlan.type === PlanTypes.STARTER
    ? activePlan.limits[LimitNames.MONTHLY_ACTIVE_USERS]
    : monthlyActiveUsersLimitPlan.getFixedLimit();

export type LegacyPlanType = {
  _links: ImmutableMap<{ self: Link }>;
  _limits: LegacyPlanLimits;
  name: string;
  version: number;
  monthlyPrice: number;
};

export class LegacyPlan extends Record<LegacyPlanType>({
  _links: Map(),
  _limits: new LegacyPlanLimits(),
  name: '',
  version: 0,
  monthlyPrice: 0,
}) {}

export { PlanTypes };

export const formatPlanType = (pt: PlanTypes): string =>
  ({
    [PlanTypes.STARTER]: 'Starter',
    [PlanTypes.STARTER2021]: 'Starter',
    [PlanTypes.STANDARD_TRIAL]: 'Professional trial',
    [PlanTypes.STANDARD_TRIAL2021]: 'Professional trial',
    [PlanTypes.PROFESSIONAL]: is2018ProTo2021PlanTransitionEnabled() ? 'Legacy professional' : 'Professional',
    [PlanTypes.PROFESSIONAL2021]: 'Professional',
    [PlanTypes.ENTERPRISE_TRIAL]: 'Enterprise trial',
    [PlanTypes.ENTERPRISE]: 'Enterprise',
    [PlanTypes.ENTERPRISE2023]: 'Enterprise',
    [PlanTypes.FOUNDATION2023]: 'Foundation',
    [PlanTypes.LEGACY]: 'Legacy',
    [PlanTypes.FOUNDATION_TRIAL2023]: 'Foundation trial',
    [PlanTypes.PARTNER]: 'Partner',
    [PlanTypes.DEVELOPER]: 'Developer',
    [PlanTypes.DEVELOPER2025]: 'Developer',
    [PlanTypes.FOUNDATION2025]: 'Foundation',
    [PlanTypes.GUARDIAN2024]: 'Guardian',
    [PlanTypes.GUARDIAN_SEATS2024]: 'Guardian',
    [PlanTypes.GUARDIAN_TRIAL2024]: 'Guardian trial',
  })[pt] ?? pt;

/**
 * Get the display name for a subscription's plan type.
 * When enableDeveloperFacade is enabled, certain plans
 * display as "Developer / Foundation" for legacy compatibility:
 * - DEVELOPER plan type (legacy developer plan facade)
 * - Foundation2023 plans with monthly billing
 *
 * When the flag is disabled, the actual plan name is shown.
 */
export const getSubscriptionPlanDisplayName = (planType: PlanTypes, billingInterval?: BillingIntervals): string => {
  if (!enableDeveloperFacade()) {
    return formatPlanType(planType);
  }

  const isLegacyDeveloperDisplay =
    planType === PlanTypes.DEVELOPER ||
    (planType === PlanTypes.FOUNDATION2023 && billingInterval === BillingIntervals.MONTHLY);

  return isLegacyDeveloperDisplay ? 'Developer / Foundation' : formatPlanType(planType);
};

export type PlanType = {
  _links: ImmutableMap<{ self: Link }>;
  limits: PlanLimits;
  basePriceInCents: BasePrice;
  type: PlanTypes;
  version: number;
};

export class Plan extends Record<PlanType>({
  _links: Map(),
  limits: new PlanLimits(),
  basePriceInCents: new BasePrice(),
  type: PlanTypes.STANDARD_TRIAL,
  version: 0,
}) {
  getMonthlyPrice() {
    return this.basePriceInCents.getMonthlyPrice() / 100;
  }
  getYearlyPrice() {
    return this.basePriceInCents.getYearlyPrice() / 100;
  }
  getType() {
    return this.type;
  }
  isStarter() {
    return this.type === PlanTypes.STARTER || this.type === PlanTypes.STARTER2021;
  }
  isEnterprise() {
    return this.type === PlanTypes.ENTERPRISE || this.type === PlanTypes.ENTERPRISE2023;
  }
  isFoundation() {
    return this.type === PlanTypes.FOUNDATION2023 || this.type === PlanTypes.FOUNDATION2025;
  }
  isDeveloper() {
    // Legacy Developer plan only (facade for Foundation2023)
    return this.type === PlanTypes.DEVELOPER;
  }
  isFreeDevPlan() {
    // Free developer plans (no payment required) - uses centralized plan metadata
    return checkIsFreeDevPlan(this.type);
  }
  isDeveloperTier() {
    // Any developer-tier plan (legacy or free)
    return this.isDeveloper() || this.isFreeDevPlan();
  }
  isGuardian() {
    return this.type === PlanTypes.GUARDIAN2024;
  }
  isProfessional() {
    return this.type === PlanTypes.PROFESSIONAL || this.type === PlanTypes.PROFESSIONAL2021;
  }

  isTrial() {
    return this.type === PlanTypes.STANDARD_TRIAL || this.type === PlanTypes.STANDARD_TRIAL2021;
  }
  is2021Plan() {
    return (
      this.type === PlanTypes.STARTER2021 ||
      this.type === PlanTypes.PROFESSIONAL2021 ||
      this.type === PlanTypes.STANDARD_TRIAL2021
    );
  }
  hasAnyConfigurableLimit() {
    // 2018 starter plans are the only plan without any configurable option;
    return this.type !== PlanTypes.STARTER;
  }
  hasConfigurableLimit(limit: LimitNames) {
    switch (limit) {
      case LimitNames.SEATS:
        return this.type !== PlanTypes.STARTER;
      case LimitNames.MONTHLY_ACTIVE_USERS:
        return !this.isStarter();
      case LimitNames.SSO:
        return this.is2021Plan();
      case LimitNames.EVENTS_RECEIVED:
        return !this.isStarter();
      case LimitNames.EXPERIMENTATION_KEYS:
        return !this.isStarter();
      case LimitNames.EVENTS_PUBLISHED:
        return this.isEnterprise();
      case LimitNames.EXPERIMENTATION_MAU:
        return this.isFoundation();
      default:
        return false;
    }
  }
}

type PlanListType = {
  items: List<Plan>;
};

export class PlanList extends Record<PlanListType>({
  items: List(),
}) {
  getDeveloperPlan() {
    let plan = this.items.filter((d) => d.type === PlanTypes.FOUNDATION2023).first<undefined>();
    plan = plan?.set('type', PlanTypes.DEVELOPER);
    return plan;
  }
  getDeveloper2025Plan() {
    return this.items.filter((d) => d.type === PlanTypes.DEVELOPER2025).first<undefined>();
  }
  getFoundationPlan() {
    return this.items.filter((d) => d.type === PlanTypes.FOUNDATION2023).first<undefined>();
  }
  getFoundation2025Plan() {
    return this.items.filter((d) => d.type === PlanTypes.FOUNDATION2025).first<undefined>();
  }
  getProfessionalPlan() {
    return this.items.filter((d) => d.type === PlanTypes.PROFESSIONAL).first<undefined>();
  }
  getProfessional2021Plan() {
    return this.items.filter((d) => d.type === PlanTypes.PROFESSIONAL2021).first<undefined>();
  }
  getStarterPlan() {
    return this.items.filter((d) => d.type === PlanTypes.STARTER).first<undefined>();
  }
  getStarter2021Plan() {
    return this.items.filter((d) => d.type === PlanTypes.STARTER2021).first<undefined>();
  }
  getEnterprisePlan() {
    return this.items.filter((d) => d.type === PlanTypes.ENTERPRISE).first<undefined>();
  }
  getGuardianPlan() {
    return this.items.filter((d) => d.type === PlanTypes.GUARDIAN2024).first<undefined>();
  }
}

export type PaymentCardType = {
  _links: ImmutableMap<{ self: Link }>;
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
};

export class PaymentCard extends Record<PaymentCardType>({
  _links: Map(),
  last4: '',
  brand: '',
  expMonth: 0,
  expYear: 0,
}) {}

type MauCountType = {
  total: number;
  anonymous: number;
  nonAnonymous: number;
  client: number;
  server: number;
};

class MauCount extends Record<MauCountType>({
  total: 0,
  anonymous: 0,
  nonAnonymous: 0,
  client: 0,
  server: 0,
}) {}

type LegacyUsageType = {
  mauCount: MauCount;
  memberCount: number;
  environmentCount: number;
  projectCount: number;
  ABTesting: boolean;
};

class LegacyUsage extends Record<LegacyUsageType>({
  mauCount: new MauCount(),
  memberCount: 0,
  environmentCount: 0,
  projectCount: 0,
  ABTesting: false,
}) {}

type SubscriptionUsageType = {
  [K in LimitNames]: number;
};

export class SubscriptionUsage extends Record<SubscriptionUsageType>(
  Object.fromEntries(Object.values(LimitNames).map((name) => [name, 0])) as SubscriptionUsageType,
) {}

export type ScheduledSubscriptionChange = ImmutableMap<{
  effectiveDate: string;
  updatedAt: string;
  primaryContext: string;
  _limits: ImmutableMap<{
    [k in LimitNames]: number;
  }>;
}>;

export type SubscriptionType = {
  _links: ImmutableMap<{ self: Link }>;
  _limits: PlanLimits;
  _limitDetails: List<ImmutableMap<LimitDetail>>;
  _hasCustomLimits: boolean;
  _next?: ScheduledSubscriptionChange | null;
  version: number;
  billingProcess: BillingProcess;
  planType: PlanTypes;
  state: SubscriptionStates;
  billingInterval: BillingIntervals;
  trialStartDate: string;
  trialEndDate: string;
  periodStartDate?: string;
  periodEndDate?: string;
  monthlyBillingExpiration?: string;
  usagePeriodStartDate?: string;
  usagePeriodEndDate?: string;
  isFree?: boolean;
  isUsageLimited?: boolean;
  trialExtensionCount: number;
  campaigns: Map<string, Campaign>;
  primaryContextKind: string;
  subscriptionType?: SubscriptionTypes;
  marketplaceProvider?: MarketplaceProviders;
};

// Per-instance memoization for getLimitDetails(). Keyed by the (immutable) Subscription
// instance, so the entry is GC'd with the instance and a new instance (new data) always
// recomputes. Avoids reallocating the array on every render-time call.
const limitDetailsCache = new WeakMap<Subscription, LimitDetail[]>();

export class Subscription extends Record<SubscriptionType>({
  billingProcess: BillingProcess.STANDARD,
  _next: null,
  _links: Map(),
  _limits: new PlanLimits(),
  _limitDetails: List(),
  _hasCustomLimits: false,
  planType: PlanTypes.STANDARD_TRIAL2021,
  version: 0,
  billingInterval: BillingIntervals.YEARLY,
  trialStartDate: '',
  trialEndDate: '',
  periodStartDate: undefined,
  periodEndDate: undefined,
  monthlyBillingExpiration: undefined,
  usagePeriodStartDate: undefined,
  usagePeriodEndDate: undefined,
  state: SubscriptionStates.UNKNOWN,
  isFree: false, // for free and invoiced customers
  isUsageLimited: false,
  trialExtensionCount: 0,
  campaigns: Map(),
  primaryContextKind: 'user',
  subscriptionType: undefined,
  marketplaceProvider: undefined,
}) {
  static states = SubscriptionStates;

  isStarter() {
    return this.planType === PlanTypes.STARTER;
  }

  isStarter2021() {
    return this.planType === PlanTypes.STARTER2021;
  }

  isFoundation() {
    return [PlanTypes.FOUNDATION2023, PlanTypes.FOUNDATION_TRIAL2023].includes(this.planType);
  }

  isProfessional() {
    return [PlanTypes.STANDARD_TRIAL, PlanTypes.PROFESSIONAL].includes(this.planType);
  }

  isProfessional2021() {
    return [PlanTypes.STANDARD_TRIAL2021, PlanTypes.PROFESSIONAL2021].includes(this.planType);
  }

  isEnterprise() {
    return [PlanTypes.ENTERPRISE_TRIAL, PlanTypes.ENTERPRISE, PlanTypes.ENTERPRISE2023].includes(this.planType);
  }

  isInvoiced() {
    return isInvoiced({ state: this.state, periodEndDate: this.periodEndDate });
  }

  hasSubscription() {
    return [
      Subscription.states.ACTIVE_SUBSCRIPTION,
      Subscription.states.ACTIVE_TRIAL_WITH_SUBSCRIPTION,
      Subscription.states.FAILED_PAYMENT_SUBSCRIPTION,
      Subscription.states.LAPSED_SUBSCRIPTION,
    ].includes(this.state);
  }

  hasPlan() {
    return this.hasSubscription() || [Subscription.states.PENDING_CANCELED_SUBSCRIPTION].includes(this.state);
  }

  isTrial() {
    return (
      this.planType === PlanTypes.STANDARD_TRIAL ||
      this.planType === PlanTypes.STANDARD_TRIAL2021 ||
      this.planType === PlanTypes.ENTERPRISE_TRIAL
    );
  }

  isActiveTrial() {
    return [Subscription.states.ACTIVE_TRIAL, Subscription.states.ACTIVE_TRIAL_WITH_SUBSCRIPTION].includes(this.state);
  }

  // Usage-based billing (UBB) trials end at midnight UTC regardless of trial end date time
  isUBBActiveTrialWithSubscription() {
    const trialEndDateObj = new Date(this.trialEndDate);
    const trialEndDateTruncatedUTC = Date.UTC(
      trialEndDateObj.getFullYear(),
      trialEndDateObj.getMonth(),
      trialEndDateObj.getDate(),
    );
    const today = new Date();
    const todayTruncatedUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    const UBBBillingPeriodHasStarted = todayTruncatedUTC >= trialEndDateTruncatedUTC;

    return (
      this.state === Subscription.states.ACTIVE_TRIAL_WITH_SUBSCRIPTION &&
      !UBBBillingPeriodHasStarted &&
      this.billingProcess === BillingProcess.USAGE_BASED_BILLING
    );
  }

  hasSubscriptionOrActiveTrial() {
    return this.hasSubscription() || this.isActiveTrial();
  }

  isActiveTrialWithNoPlan() {
    return this.state === Subscription.states.ACTIVE_TRIAL;
  }

  isActiveTrialWithPlan() {
    return this.state === SubscriptionStates.ACTIVE_TRIAL_WITH_SUBSCRIPTION;
  }

  isExpiredTrialOrCanceled() {
    return [
      Subscription.states.GRACE_PERIOD_TRIAL,
      Subscription.states.LAPSED_TRIAL,
      Subscription.states.LAPSED_SUBSCRIPTION,
    ].includes(this.state);
  }

  isReadOnly() {
    if (!enableUsageExceededSelectiveReadOnly()) {
      return this.state === Subscription.states.USAGE_EXCEEDED_READ_ONLY || this.isExpiredTrialOrCanceled();
    }
    return this.isExpiredTrialOrCanceled();
  }

  isExpiredTrialWithNoPlan() {
    return isExpiredTrialWithNoPlan({ state: this.state });
  }

  isPendingCancelation() {
    return this.state === Subscription.states.PENDING_CANCELED_SUBSCRIPTION;
  }

  isCancelled() {
    return this.state === Subscription.states.CANCELED_SUBSCRIPTION;
  }

  isLapsed() {
    return this.state === Subscription.states.LAPSED_SUBSCRIPTION;
  }

  hasFailedPayment() {
    return this.state === Subscription.states.FAILED_PAYMENT_SUBSCRIPTION;
  }

  isCanceledOrLapsed() {
    return this.isCancelled() || this.isLapsed();
  }

  hasUpcomingPayment() {
    return (
      [
        Subscription.states.ACTIVE_TRIAL_WITH_SUBSCRIPTION,
        Subscription.states.ACTIVE_SUBSCRIPTION,
        Subscription.states.FAILED_PAYMENT_SUBSCRIPTION,
      ].includes(this.state) && !checkIsFreeDevPlan(this.planType)
    );
  }

  hasOverduePayment() {
    return this.state === Subscription.states.FAILED_PAYMENT_SUBSCRIPTION;
  }

  isYearly() {
    return this.billingInterval === BillingIntervals.YEARLY;
  }

  canUpdateBillingInterval() {
    if (switchBillingYearlyToMonthlyGracePeriod() !== 0) {
      if (!isNil(this.monthlyBillingExpiration) && new Date().valueOf() < Date.parse(this.monthlyBillingExpiration)) {
        return true;
      }
    }
    return !(this.hasPlan() && this.billingInterval === BillingIntervals.YEARLY);
  }

  updateBillingIntervalExpiration() {
    return !isNil(this.monthlyBillingExpiration) && switchBillingYearlyToMonthlyGracePeriod() !== 0
      ? Date.parse(this.monthlyBillingExpiration)
      : null;
  }

  isSelfServe() {
    return isSelfServe({ state: this.state, periodEndDate: this.periodEndDate });
  }

  canMakeSelfServeChanges() {
    return canMakeSelfServeChanges({
      periodEndDate: this.periodEndDate,
      _hasCustomLimits: this._hasCustomLimits,
      state: this.state,
      planType: this.planType as unknown as PlanTypes,
    });
  }

  cannotUpdateSubscription() {
    return !this.canMakeSelfServeChanges();
  }

  isCustomPlan() {
    return this._hasCustomLimits;
  }

  is2021Plan() {
    return is2021Plan({ state: this.state, planType: this.planType as unknown as PlanTypes });
  }

  isUsageBasedBilling() {
    return (
      this.billingProcess === BillingProcess.USAGE_BASED_BILLING ||
      this.billingProcess === BillingProcess.COMMIT_WITH_OVERAGE
    );
  }
  hasSSO() {
    return this._limits.SSO === 1;
  }

  getPerSeatPrice() {
    if (!this.is2021Plan()) {
      return;
    }
    const cost = this.planType === PlanTypes.STARTER2021 ? 10 : 20;
    return this.hasSSO() ? cost + 10 : cost;
  }

  getSeatCount() {
    return this._limits.SeatCount;
  }

  getTrialDaysRemaining() {
    return getTrialDaysRemaining({
      state: this.state,
      trialEndDate: this.trialEndDate,
      campaigns: this.campaigns.toJS(),
    });
  }

  getSeatLimit() {
    return getSeatLimit({ state: this.state, _limits: this._limits.toJS() });
  }

  getEnterpriseCampaign() {
    const campaign = getEnterpriseCampaign({ state: this.state, campaigns: this.campaigns.toJS() });
    if (campaign) {
      // conversion of plainJS to class
      return new Campaign(campaign);
    }
    return;
  }

  canStartEnterpriseCampaign() {
    const enterpriseCampaign = this.getEnterpriseCampaign();
    const enterpriseCampaignNotActive = !enterpriseCampaign?.isActive();
    return (
      [PlanTypes.PROFESSIONAL, PlanTypes.PROFESSIONAL2021, PlanTypes.LEGACY].includes(this.planType) &&
      enterpriseCampaignNotActive
    );
  }

  isDefaultedOrActiveTrialWithNoPlan() {
    return (
      this.isExpiredTrialOrCanceled() || this.isActiveTrialWithNoPlan() || !!this.getEnterpriseCampaign()?.isActive()
    );
  }

  isEnterpriseTrial() {
    return this.planType === PlanTypes.ENTERPRISE_TRIAL || this.hasActiveEnterpriseCampaign();
  }

  hasActiveEnterpriseCampaign() {
    return !!this.getEnterpriseCampaign()?.isActive();
  }

  hasExpiredEnterpriseCampaign() {
    return !!this.getEnterpriseCampaign()?.isExpired();
  }

  isMarketplace() {
    return this.subscriptionType === SubscriptionTypes.MARKETPLACE;
  }

  isVercel() {
    return this.marketplaceProvider === MarketplaceProviders.VERCEL && this.isMarketplace();
  }

  // Returns the backend-provided limits as a plain, display-ordered array. This is
  // the source of truth for which limits to render and in what order, replacing the
  // frontend BILLING_LIMIT_ORDER constant.
  getLimitDetails(): LimitDetail[] {
    const cached = limitDetailsCache.get(this);
    if (cached) {
      return cached;
    }
    const details = this._limitDetails.toJS() as LimitDetail[];
    limitDetailsCache.set(this, details);
    return details;
  }
}

type LegacySubscriptionType = {
  _links: ImmutableMap<{ self: Link; plan: Link }>;
  _limits: LegacyPlanLimits;
  _usage: LegacyUsage;
  name: string;
  version: number;
  trialStartDate: string;
  trialEndDate: string;
  periodStartDate?: string;
  periodEndDate?: string;
  graceStartDate?: string;
  graceEndDate?: string;
  seatCount?: number;
  isBeta: boolean;
  isTrial: boolean;
  isActive: boolean;
  isCurrent: boolean;
  isCanceled: boolean;
  isUsageLimited: boolean;
};

export class LegacySubscription extends Record<LegacySubscriptionType>({
  _links: Map(),
  _limits: new LegacyPlanLimits(),
  _usage: new LegacyUsage(),
  name: '',
  version: 0,
  trialStartDate: '',
  trialEndDate: '',
  periodStartDate: undefined,
  periodEndDate: undefined,
  graceStartDate: undefined,
  graceEndDate: undefined,
  seatCount: undefined,
  // isBeta is a legacy status that was equivalent to a free unlimited subscription.
  isBeta: false,
  isTrial: false,
  isActive: false,
  isCurrent: false,
  isCanceled: false,
  isUsageLimited: false,
}) {
  hasPlan() {
    return hasPlan({ _links: this._links?.toJS() as { [key: string]: { href?: string; type?: string } } | undefined });
  }

  hasABTesting() {
    return this._limits.abTesting;
  }

  hasTeams() {
    return this._limits.teams;
  }

  hasCustomRoles() {
    return this._limits.customRoles;
  }

  isInvoiced() {
    return !this.periodEndDate && this.hasPlan();
  }

  hasMultipleEnvironments() {
    return this._limits.multipleEnvironments;
  }

  hasAuditLog() {
    return this._limits.auditLog;
  }

  // A trial is expired if it ended before today.
  isTrialExpired() {
    return isTrialExpired({ trialEndDate: this.trialEndDate });
  }

  isActiveTrial() {
    return !this.isTrialExpired() && !this.isCanceled && !this.isBeta;
  }

  isActiveTrialWithNoPlan() {
    return this.hasPlan() && this.isTrial;
  }

  hasSubscriptionOrActiveTrial() {
    return this.hasSubscription() || this.isActiveTrial();
  }

  isExpiredTrialWithNoPlan() {
    return isExpiredTrialWithNoPlan({
      isCanceled: this.isCanceled,
      isBeta: this.isBeta,
      trialEndDate: this.trialEndDate,
      _links: this._links.toJS() as { [key: string]: { href?: string; type?: string } } | undefined,
    });
  }

  isLapsed() {
    return !this.isActive;
  }

  isExpiredTrialOrCanceled() {
    return (this.isTrial && this.isTrialExpired()) || this.isCanceled;
  }

  isReadOnly() {
    return this.isExpiredTrialOrCanceled();
  }

  getPlanName() {
    if (this.isBeta) {
      return 'Custom plan';
    }

    return this.hasPlan() ? titlecase(this.name) : 'Trial';
  }

  isCancelled() {
    return this.isCanceled;
  }

  hasUpcomingPayment() {
    return this.hasPlan() && !this.isCanceled && !this.isLapsed();
  }

  hasOverduePayment() {
    return this.hasPlan() && !this.isCurrent;
  }

  hasSubscription() {
    return !this.isCanceled && this.hasPlan();
  }

  getTrialDaysRemaining() {
    return getTrialDaysRemaining({ trialEndDate: this.trialEndDate });
  }

  getEnterpriseCampaign(): undefined {
    // not supporrted on legacy subscriptions
    return;
  }

  isDefaultedOrActiveTrialWithNoPlan() {
    return this.isExpiredTrialOrCanceled() || this.isActiveTrialWithNoPlan();
  }

  isEnterpriseTrial() {
    // not supported on legacy subscriptions
    return false;
  }

  hasActiveEnterpriseCampaign() {
    // not supported on legacy subscriptions
    return false;
  }

  hasExpiredEnterpriseCampaign() {
    // not supported on legacy subscriptions
    return false;
  }

  isSelfServe() {
    // not supported on legacy subscriptions
    return false;
  }

  canMakeSelfServeChanges() {
    // not supported on legacy subscriptions
    return false;
  }
}

export function getPlan(subscription: LegacySubscription, publicPlans: List<LegacyPlan>) {
  const currentPlanId = subscription.getIn(['_links', 'plan', 'href']);
  return publicPlans.find((plan) => plan.getIn(['_links', 'self', 'href']) === currentPlanId);
}

// A custom plan is a plan that is not publicly available.
export function isCustomPlan(subscription: LegacySubscription, publicPlans: List<LegacyPlan>) {
  return subscription.hasPlan() && !getPlan(subscription, publicPlans);
}

export function hasAnySubscription(subscription?: Subscription, legacySubscription?: LegacySubscription) {
  return (
    (subscription && subscription.hasSubscription()) || (legacySubscription && legacySubscription.hasSubscription())
  );
}

type InvoiceType = {
  _links: ImmutableMap<{ self: Link; doc: Link }>;
  date: string;
  key?: string;
};

export class Invoice extends Record<InvoiceType>({
  _links: Map(),
  date: '',
  key: '',
}) {
  selfLink() {
    return this._links.getIn(['self', 'href']);
  }

  docLink() {
    return this._links.getIn(['doc', 'href']);
  }
}

type AccruedInvoiceType = {
  amount: number;
  lines: List<AccruedInvoiceLineType>;
  discount: number | null;
  status: string;
  issueDate: number;
  periodStart: number;
  periodEnd: number;
  isCurrent: boolean;
  createdAt: number;
  id: string;
  partiallyInvoicedAmount: number | null;
  subtotal: number;
  _links: ImmutableMap<{ self: Link }>;
};

export class AccruedInvoice extends Record<AccruedInvoiceType>({
  amount: 0,
  lines: List(),
  discount: null,
  status: '',
  issueDate: 0,
  periodStart: 0,
  periodEnd: 0,
  isCurrent: false,
  createdAt: 0,
  id: '',
  partiallyInvoicedAmount: null,
  subtotal: 0,
  _links: Map(),
}) {
  selfLink() {
    return this._links.getIn(['self', 'href']);
  }
}

type AccruedInvoiceLineType = {
  amount: number;
  billedQuantity: number;
  commitQuantity: number;
  discount: AccruedInvoiceLineDiscountType | null;
  freeQuantity: number;
  lineId: string;
  overageQuantity: number;
  packageQuantity: number;
  periodEnd: number;
  periodStart: number;
  perUnitAmount: number | null;
  primaryContextKind: string | null;
  productId: string;
  productName: string;
  productType: string;
  subtotal: number;
  usageQuantity: number;
  partiallyInvoicedAmount: number | null;
};

export class AccruedInvoiceLine extends Record<AccruedInvoiceLineType>({
  amount: 0,
  billedQuantity: 0,
  commitQuantity: 0,
  discount: null,
  freeQuantity: 0,
  lineId: '',
  overageQuantity: 0,
  packageQuantity: 0,
  periodEnd: 0,
  periodStart: 0,
  perUnitAmount: null,
  primaryContextKind: null,
  productId: '',
  productName: '',
  productType: '',
  subtotal: 0,
  usageQuantity: 0,
  partiallyInvoicedAmount: null,
}) {}

type AccruedInvoiceLineDiscountType = {
  discountAmount: number;
  discountQuantity: number | null;
};

export class AccruedInvoiceLineDiscount extends Record<AccruedInvoiceLineDiscountType>({
  discountAmount: 0,
  discountQuantity: null,
}) {}

// Limits shown in checkout but not yet billed.
// TODO remove when AI runs billing is released
const DISPLAY_ONLY_LIMIT_NAMES_LEGACY: ReadonlySet<LimitNames> = new Set([LimitNames.AI_RUNS]);

export type SubscriptionChangeType = {
  planType: PlanTypes | null;
  billingInterval: BillingIntervals | null;
  billingProcess: BillingProcess | null;
  limits: PlanLimits;
};

export class SubscriptionChange extends Record<SubscriptionChangeType>({
  planType: null,
  billingInterval: null,
  billingProcess: null,
  limits: new PlanLimits(),
}) {
  areLimitsEmpty() {
    return Object.values(LimitNames).every((v) => isNil(this.limits[v]));
  }
  areLimitsZero() {
    return Object.values(LimitNames).every((v) => this.limits[v] === 0);
  }
  hasSSO() {
    return this.limits.SSO === 1;
  }
  getSeatCount() {
    return this.limits.SeatCount;
  }
  isYearly() {
    return this.billingInterval === BillingIntervals.YEARLY;
  }
  isMonthly() {
    return this.billingInterval === BillingIntervals.MONTHLY;
  }
  toRepLimits() {
    let rep = Map<LimitNames, number>();

    for (const k of objectKeys(this.limits.toObject())) {
      if (DISPLAY_ONLY_LIMIT_NAMES_LEGACY.has(k)) {
        continue;
      }
      const v = this.limits[k];
      if (!isNil(v) && v >= 0) {
        rep = rep.set(k, v);
      }
    }

    return rep;
  }

  isDeveloperPlan() {
    return this.planType === PlanTypes.DEVELOPER;
    // a Developer Plan is a monthly Foundation Plan
  }

  isFoundationPlan() {
    return this.planType === PlanTypes.FOUNDATION2023;
  }

  isProfessionalPlan() {
    return this.planType === PlanTypes.PROFESSIONAL || this.planType === PlanTypes.PROFESSIONAL2021;
  }

  isEnterprisePlan() {
    return this.planType === PlanTypes.ENTERPRISE || this.planType === PlanTypes.ENTERPRISE2023;
  }

  isGuardianPlan() {
    return this.planType === PlanTypes.GUARDIAN2024;
  }

  toRep() {
    let rep: ImmutableMap<{
      planType?: PlanTypes;
      billingInterval?: BillingIntervals;
      billingProcess?: BillingProcess;
      limits?: Map<LimitNames, number>;
    }> = Map();

    if (this.planType) {
      rep = rep.set('planType', this.planType);
    }

    if (this.billingInterval) {
      rep = rep.set('billingInterval', this.billingInterval);
    }

    if (this.billingProcess) {
      rep = rep.set('billingProcess', this.billingProcess);
    }

    if (
      enableSelfServeUBBWithAnnualCommits() &&
      this.planType === PlanTypes.FOUNDATION2023 &&
      this.billingInterval === BillingIntervals.YEARLY
    ) {
      // Foundation annual commits should send monthly active users, HOSTS.
      const limits = [
        LimitNames.MONTHLY_ACTIVE_USERS,
        LimitNames.HOSTS,
        LimitNames.SESSIONS,
        LimitNames.ERRORS,
        LimitNames.TRACES,
        LimitNames.LOGS,
      ];

      // It should also send Experimentation Keys if enableSelfServeExperimentationKeysLimit flag is serving TRUE.
      if (enableSelfServeExperimentationKeysLimit()) {
        limits.push(LimitNames.EXPERIMENTATION_KEYS);
      }

      rep = rep.set(
        'limits',
        this.toRepLimits().filter((value, key) => limits.includes(key)),
      );
    } else if (this.planType !== PlanTypes.STARTER) {
      const limitRep = this.toRepLimits();
      if (!limitRep.isEmpty()) {
        rep = rep.set('limits', limitRep);
      }
    } else {
      rep = rep.remove('limits');
    }

    return rep.toJS();
  }
}

type SubscriptionPriceType = {
  monthly: number;
  yearly: number;
  promoCode?: string;
};

export class SubscriptionPrice extends Record<SubscriptionPriceType>({
  monthly: 0,
  yearly: 0,
  promoCode: '',
}) {
  getMonthlyPrice() {
    return this.monthly / 100;
  }

  getYearlyPrice() {
    return this.yearly / 100;
  }
}

export type SubscriptionPreview = ImmutableMap<{
  prorationTime: number;
  prices?: SubscriptionPrice;
  proratedPrices?: SubscriptionPrice;
  creditDue?: SubscriptionPrice;
  renewalTime: number;
}>;

export const HumanCampaignNames: { [key in Campaigns]: string } = {
  [Campaigns.ENTERPRISE]: 'enterprise',
  [Campaigns.AI_CONFIGS]: 'AgentControl',
};

type CampaignType = {
  startDateTime: string;
  endDateTime: string;
  extensionCount: number;
};

export class Campaign extends Record<CampaignType>({
  startDateTime: '',
  endDateTime: '',
  extensionCount: 0,
}) {
  getStartDate() {
    return getCampaignStartDate({
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
      extensionCount: this.extensionCount,
    });
  }

  getEndDate() {
    return getCampaignEndDate({
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
      extensionCount: this.extensionCount,
    });
  }

  daysRemaining() {
    return campaignDaysRemaining({
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
      extensionCount: this.extensionCount,
    });
  }

  isActive() {
    return isCampaignActive({
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
      extensionCount: this.extensionCount,
    });
  }

  isExpired() {
    return isCampaignExpired({
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
      extensionCount: this.extensionCount,
    });
  }

  hasBeenStarted() {
    return hasCampaignBeenStarted({
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
      extensionCount: this.extensionCount,
    });
  }

  hasBeenExtended() {
    return hasCampaignBeenExtended({
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
      extensionCount: this.extensionCount,
    });
  }
}

export function createCampaign(props?: CreateFunctionInput<CampaignType>) {
  return new Campaign(fromJS(props));
}

export function createPlanLimits(props?: CreateFunctionInput<PlanLimits>) {
  return new PlanLimits(fromJS(props));
}

function createBasePrice(props?: CreateFunctionInput<BasePrice>) {
  return new BasePrice(props);
}

function createLegacyPlanLimits(props?: CreateFunctionInput<LegacyPlanLimits>) {
  return new LegacyPlanLimits(props);
}

export function createSubscriptionUsage(props?: CreateFunctionInput<SubscriptionUsage>) {
  return props ? new SubscriptionUsage(props) : new SubscriptionUsage();
}

function createLegacyUsage(props?: CreateFunctionInput<LegacyUsage>) {
  return props ? new LegacyUsage(fromJS(props).update('mauCount', createMauCount)) : new LegacyUsage();
}

function createMauCount(props: CreateFunctionInput<MauCount>) {
  return new MauCount(props);
}

export function createLegacyPlan(props?: CreateFunctionInput<LegacyPlan>) {
  return props ? new LegacyPlan(fromJS(props).update('_limits', createLegacyPlanLimits)) : new LegacyPlan();
}

export function createPlan(props?: CreateFunctionInput<Plan>) {
  return props
    ? new Plan(fromJS(props)).update('limits', createPlanLimits).update('basePriceInCents', createBasePrice)
    : new Plan();
}

export function createPlanList(props?: CreateFunctionInput<PlanList>) {
  return props ? new PlanList(fromJS(props)).update('items', (items) => items.map(createPlan)) : new PlanList();
}

export function createLimitPlans(props?: CreateFunctionInput<LimitPlans>) {
  if (!props) {
    return new LimitPlans();
  }
  let plans = new LimitPlans(fromJS(props));
  for (const key of Object.values(LimitNames)) {
    plans = plans.update(key, createLimitPlan);
  }
  return plans;
}

export function createLimitTier(props?: CreateFunctionInput<LimitTier>) {
  return props ? new LimitTier(fromJS(props)) : new LimitTier();
}

export function createLimitPlan(props?: CreateFunctionInput<LimitPlan>) {
  return props
    ? new LimitPlan(fromJS(props)).update('tiers', (tiers) => (tiers ? tiers.map(createLimitTier) : List()))
    : new LimitPlan();
}

export function createPaymentCard(props?: CreateFunctionInput<PaymentCard>) {
  return props ? new PaymentCard(fromJS(props)) : new PaymentCard();
}

export function createLegacySubscription(props?: CreateFunctionInput<LegacySubscription>) {
  return props
    ? new LegacySubscription(
        fromJS(props).update('_limits', createLegacyPlanLimits).update('_usage', createLegacyUsage),
      )
    : new LegacySubscription();
}

export function createSubscription(props?: CreateFunctionInput<Subscription>) {
  return props
    ? new Subscription(fromJS(props))
        .update('_limits', createPlanLimits)
        .update('campaigns', (campaigns) => (campaigns ? campaigns.map(createCampaign) : Map()))
    : new Subscription();
}

export function createInvoice(props?: CreateFunctionInput<Invoice>) {
  return props instanceof Invoice ? props : new Invoice(fromJS(props));
}

export function createAccruedInvoice(props?: CreateFunctionInput<AccruedInvoice>) {
  return props instanceof AccruedInvoice
    ? props
    : new AccruedInvoice(fromJS(props)).update('lines', (lines) => lines.map(createLineItem));
}

export function createLineItem(props?: CreateFunctionInput<AccruedInvoiceLine>) {
  return props instanceof AccruedInvoiceLine ? props : new AccruedInvoiceLine(fromJS(props));
}

export function createLineItemDiscount(props?: CreateFunctionInput<AccruedInvoiceLineDiscount>) {
  return props instanceof AccruedInvoiceLineDiscount ? props : new AccruedInvoiceLineDiscount(fromJS(props));
}

export const createSubscriptionChange = (props?: CreateFunctionInput<SubscriptionChange>) =>
  props instanceof SubscriptionChange
    ? props
    : new SubscriptionChange(fromJS(props)).update('limits', createPlanLimits);

export const createSubscriptionPrice = (props?: CreateFunctionInput<SubscriptionPrice>) =>
  props instanceof SubscriptionPrice ? props : new SubscriptionPrice(fromJS(props));

const getLimitChange = (subscriptionChange: SubscriptionChange) =>
  subscriptionChange.limits.toSeq().toMap().filter(valueExists);

export const isSubscriptionDirty = ({
  subscription,
  subscriptionChange,
  activePlan,
}: {
  subscription: Subscription;
  subscriptionChange?: SubscriptionChange;
  activePlan?: Plan;
}) => {
  if (!subscriptionChange) {
    return false;
  }
  const limitChange = getLimitChange(subscriptionChange);
  const hasCurrentAnnualFoundationPlan =
    subscription.planType === PlanTypes.FOUNDATION2023 && subscription.billingInterval === BillingIntervals.YEARLY;
  const hasSelectedAnnualFoundationPlan =
    subscriptionChange.planType === PlanTypes.FOUNDATION2023 &&
    subscriptionChange.billingInterval === BillingIntervals.YEARLY;

  const nextChanges = subscription._next?.get('_limits');

  if (hasCurrentAnnualFoundationPlan && hasSelectedAnnualFoundationPlan && nextChanges) {
    // Check if subscription changes currently in browser match the upcoming scheduled subscription changes from the backend (in _next). If these match, return false, else return true.
    return !nextChanges?.every((value, key) => limitChange.get(key) === value);
  }

  // Legacy Developer plan is a facade for FOUNDATION2023, so check if planType changed
  if (activePlan?.isDeveloper()) {
    return (
      subscription.planType !== PlanTypes.FOUNDATION2023 ||
      subscription.billingInterval !== subscriptionChange.billingInterval
    );
  }

  // Free developer plans are fixed plans with no configurable limits
  if (activePlan?.isFreeDevPlan()) {
    return (
      subscription.planType !== activePlan?.type || subscription.billingInterval !== subscriptionChange.billingInterval
    );
  }

  return (
    subscription.planType !== activePlan?.type ||
    subscription.billingInterval !== subscriptionChange.billingInterval ||
    !limitChange.every((value, key) => subscription._limits.get(key) === value)
  );
};

export const shouldShowManagePlanPageLeaveConfirmation = ({
  subscription,
  subscriptionChange,
}: {
  subscription: Subscription;
  subscriptionChange?: SubscriptionChange;
}) => {
  if (!subscriptionChange) {
    return false;
  }
  const limitChange = getLimitChange(subscriptionChange);
  const initialAddOnValues =
    limitChange.get(LimitNames.SEATS) === (enableSelfServeUBBWithAnnualCommits() ? 0 : 5) &&
    (limitChange.get(LimitNames.MONTHLY_ACTIVE_USERS) === 10000 ||
      limitChange.get(LimitNames.MONTHLY_ACTIVE_USERS) === 1000) &&
    limitChange.get(LimitNames.SSO) === 0 &&
    limitChange.get(LimitNames.EVENTS_PUBLISHED) === 0 &&
    limitChange.get(LimitNames.EXPERIMENTATION_KEYS) === 0 &&
    limitChange.get(LimitNames.EVENTS_RECEIVED) === 0;

  if (subscription.isTrial() && initialAddOnValues) {
    return false;
  }

  return (
    subscription.billingInterval !== subscriptionChange.billingInterval ||
    !limitChange.every((value, key) => subscription._limits.get(key) === value)
  );
};

export const isUpgradeNeededToAddSeats = ({
  isSeatLimitEnforced,
  numberOfNewSeatsToPurchase,
  planHasUnlimitedSeats,
  planSeatLimit,
  subscription,
  subscriptionSeatLimit,
}: {
  isSeatLimitEnforced: boolean;
  numberOfNewSeatsToPurchase: number;
  planHasUnlimitedSeats: boolean;
  planSeatLimit: number;
  subscription: Subscription;
  subscriptionSeatLimit: number;
}) => {
  if (!isSeatLimitEnforced || planHasUnlimitedSeats || numberOfNewSeatsToPurchase <= 0) {
    return false;
  }

  // If they invited everyone in the form (in addition to people already on their team) they'd use this many seats:
  const numberOfTotalSeatsAfterPurchase = subscriptionSeatLimit + numberOfNewSeatsToPurchase;

  // They have this many seats available before they need to upgrade to the next level:
  const seatsAvailableToPurchaseOnCurrentPlan = planSeatLimit - numberOfTotalSeatsAfterPurchase;

  return (
    [PlanTypes.STARTER2021, PlanTypes.PROFESSIONAL2021].includes(subscription.planType) &&
    seatsAvailableToPurchaseOnCurrentPlan < 0
  );
};

type SanitizedPlanLimits = {
  [key in LimitNames]?: number;
};

function filterOutNilLimits(limits: PlanLimits): SanitizedPlanLimits {
  return limits.toSeq().toMap().filter(valueExists).toObject();
}

// The UI does not support creating enterprise subscriptions, so it's ignored here.
export const createSubscriptionChangeFromSelection = (
  subscription: Subscription,
  activePlan: Plan | null | undefined,
  limitPlans: LimitPlans,
  selectedLimits: PlanLimits,
  selectedBillingInterval: BillingIntervals | null,
  usage: SubscriptionUsage,
) => {
  if (!activePlan) {
    return;
  }

  let baseLimits: SanitizedPlanLimits = {};
  if (activePlan.isProfessional() || activePlan.type === PlanTypes.STARTER2021) {
    baseLimits = {
      [LimitNames.SEATS]: limitPlans.get(LimitNames.SEATS).getLowerLimit(),
    };
  }

  let limits;
  //If on same plan as subscription, use selected limits
  if (subscription.hasPlan() && subscription.planType === activePlan.type) {
    limits = subscription._limits;
  } else {
    //If switching plans, use the current plan usage
    limits = preSetLimitsBasedOnUsage(usage, limitPlans, subscription, activePlan);
  }

  return createSubscriptionChange({
    planType: activePlan.type,
    billingInterval: getSelectedBillingInterval(activePlan, subscription, selectedBillingInterval),
    billingProcess:
      (enableSelfServeUBBWithAnnualCommits() && activePlan.isFoundation()) ||
      (enableDeveloperPlanCheckout() && activePlan.isDeveloper())
        ? BillingProcess.COMMIT_WITH_OVERAGE
        : null,
    // Only include limits for plans that support them (not fixed free developer plans)
    ...(activePlan.isFreeDevPlan()
      ? {}
      : {
          limits: {
            ...baseLimits,
            // TODO We should not have to cast {} to PlanLimitsType: it's not accurate, but we currently need to because we mix plan limits from different contexts
            ...(limits ? limits.toObject() : ({} as PlanLimitsType)),
            ...filterOutNilLimits(selectedLimits),
          },
        }),
  });
};

export function derivePlanFromLegacyPlan({
  usage,
  limitPlans,
  plans,
}: {
  usage: SubscriptionUsage;
  limitPlans: LimitPlans;
  plans: PlanList;
}) {
  if (Object.values(usage.toObject()).reduce((a, b) => a + b) === 1) {
    return plans.getStarter2021Plan();
  } else if (
    usage.SeatCount >= limitPlans.SeatCount.getUpperLimit() ||
    usage.MonthlyActiveUsers >= limitPlans.MonthlyActiveUsers.getUpperLimit() ||
    (usage.EventsReceived !== 0 && usage.EventsReceived >= limitPlans.EventsReceived.getUpperLimit())
  ) {
    return plans.getEnterprisePlan();
  }
  return plans.getProfessional2021Plan();
}

// Map legacy plan types to newer versions when feature flags are enabled
function normalizePlanType(planType: PlanTypes, plans: PlanList): PlanTypes {
  // Guardian trial users should see Developer2025 as the default option
  if (
    planType === PlanTypes.GUARDIAN_TRIAL2024 &&
    enableSelfServeDeveloper2025PlanCheckout() &&
    plans.getDeveloper2025Plan()
  ) {
    return PlanTypes.DEVELOPER2025;
  }

  return planType;
}

export function findSelectedPlan({
  planType,
  plans,
  usage,
  limitPlans,
}: {
  planType: PlanTypes;
  plans: PlanList;
  usage: SubscriptionUsage;
  limitPlans: LimitPlans;
}) {
  // Normalize plan type based on feature flags
  const normalizedPlanType = normalizePlanType(planType, plans);

  switch (normalizedPlanType) {
    case PlanTypes.DEVELOPER:
      return plans.getDeveloperPlan();
    case PlanTypes.DEVELOPER2025:
      return plans.getDeveloper2025Plan();
    case PlanTypes.FOUNDATION_TRIAL2023:
      return plans.getFoundationPlan();
    case PlanTypes.FOUNDATION2023:
      return plans.getFoundationPlan();
    case PlanTypes.FOUNDATION2025:
      return plans.getFoundation2025Plan();
    case PlanTypes.ENTERPRISE:
      return plans.getEnterprisePlan();
    case PlanTypes.ENTERPRISE_TRIAL:
      return plans.getEnterprisePlan();
    case PlanTypes.GUARDIAN2024:
      return plans.getGuardianPlan();
    case PlanTypes.GUARDIAN_TRIAL2024:
      return plans.getDeveloperPlan();
    case PlanTypes.STANDARD_TRIAL:
      return plans.getProfessionalPlan();
    case PlanTypes.PROFESSIONAL:
      return plans.getProfessionalPlan();
    case PlanTypes.STANDARD_TRIAL2021:
      return plans.getProfessional2021Plan();
    case PlanTypes.PROFESSIONAL2021:
      return plans.getProfessional2021Plan();
    case PlanTypes.STARTER:
      return plans.getStarterPlan();
    case PlanTypes.STARTER2021:
      return plans.getStarter2021Plan();
    case PlanTypes.LEGACY:
      return derivePlanFromLegacyPlan({ usage, limitPlans, plans });
    default:
      return;
  }
}

function findClosestMaxValue(tiers: LimitTier[], usage: number) {
  let limit;
  tiers.forEach((t, i) => {
    if (usage !== null && tiers[i + 1]) {
      if (usage === t.limit) {
        limit = t.limit;
      } else if (usage > t.limit && usage >= tiers[i + 1].limit) {
        limit = tiers[tiers.length - 1].limit;
      } else if (usage > t.limit && usage <= tiers[i + 1].limit) {
        limit = tiers[i + 1].limit;
      }
    }
  });
  return limit || (tiers.length ? tiers[0].limit : 0);
}

export function preSetLimitsBasedOnUsage(
  usage: SubscriptionUsage,
  limitPlans: LimitPlans,
  subscription?: Subscription,
  activePlan?: Plan,
) {
  let newLimits = createPlanLimits();
  (Object.keys(limitPlans.toObject()) as LimitNames[]).forEach((key: LimitNames) => {
    newLimits = newLimits.set(key, findClosestMaxValue(limitPlans.get(key).tiers.toArray(), usage[key]));
  });
  const subscriptionSeatCount = subscription?.getSeatCount();
  const updatedLimitsSeatCount = newLimits.SeatCount;
  // For new plans with seats in increments of 1 we set the seats to the purchased amount rather than usage amount
  // if the subscribed limit does not exceed the available limit for that plan
  if (activePlan?.is2021Plan() && subscriptionSeatCount && updatedLimitsSeatCount) {
    const subscriptionSeatLimitExceedsMaxSeatLimit = limitPlans.SeatCount.getUpperLimit() < subscriptionSeatCount;
    if (subscriptionSeatCount > updatedLimitsSeatCount && !subscriptionSeatLimitExceedsMaxSeatLimit) {
      newLimits = newLimits.set(LimitNames.SEATS, subscriptionSeatCount);
    }
  }
  // avoid removing SSO via switching plans
  if (subscription?.hasSSO()) {
    newLimits = newLimits.set(LimitNames.SSO, 1);
  }
  return newLimits;
}

export function getUsageOverageList({
  subscriptionUsage,
  activePlan,
  selectedExperimentation,
  selectedClientMAUs,
  limitPlans,
}: {
  subscriptionUsage: SubscriptionUsage;
  activePlan: Plan;
  selectedExperimentation: number | undefined;
  selectedClientMAUs: number | undefined;
  limitPlans: LimitPlans;
}) {
  const selectedLimits = [selectedExperimentation, selectedClientMAUs];
  const limitNameList = [LimitNames.EVENTS_RECEIVED, LimitNames.MONTHLY_ACTIVE_USERS];

  const usageTypesOverLimit = limitNameList.filter((ln, i) => {
    if (!activePlan.hasAnyConfigurableLimit()) {
      //use 2018 starter plan limits for comparison
      const limit = activePlan.limits[ln];
      return !!limit && subscriptionUsage[ln] > limit;
    } else {
      const limit = selectedLimits[i];
      return limit ? subscriptionUsage[ln] > limit : subscriptionUsage[ln] > limitPlans[ln].getLowerLimit();
    }
  });
  return usageTypesOverLimit;
}

export function showSeatLimitError({
  subscription,
  subscriptionUsage,
  activePlan,
  selectedSeat,
}: {
  subscription: Subscription;
  subscriptionUsage: SubscriptionUsage;
  activePlan: Plan;
  selectedSeat: number | undefined;
}) {
  if (!activePlan.hasAnyConfigurableLimit()) {
    const seats = activePlan.limits[LimitNames.SEATS];
    return !isNil(seats) && subscriptionUsage.SeatCount > seats;
  } else if (activePlan.isEnterprise()) {
    return false;
  } else if (activePlan.isFoundation()) {
    return false;
  }

  //for Standard Trial and Professional plans
  if (selectedSeat) {
    return !activePlan.isEnterprise() && subscriptionUsage.SeatCount > selectedSeat;
  }

  const seats = subscription._limits[LimitNames.SEATS];
  return !isNil(seats) && !activePlan.isEnterprise() && subscriptionUsage.SeatCount > seats;
}

export const computeLimitRatio = (usage: number, limit: number) => (limit === -1 ? Infinity : usage / limit);

export const computePlanLimit = (usage: number, limit: number | null) => {
  if (!limit || limit === -1) {
    return null;
  }
  const limitRatio = computeLimitRatio(usage, limit);
  return limitRatio > USAGE_WARNING_THRESHOLD / 100 ? limit : null;
};

export const trackBillingEvent = createTrackerForCategory('Billing');

export const trackPaymentMethodButtonClick = (attrs: object) =>
  trackBillingEvent('Edit Payment Method Button Clicked', attrs);

export const trackPlanPickerButtonClick = (component: string, cta: string) =>
  trackBillingEvent('Plan Picker Button Clicked', { component, cta });
export const trackBillingContactButtonClick = (attrs: object) =>
  trackBillingEvent('Edit Billing Contact Button Clicked', attrs);
export const trackSavePaymentButtonClicked = (attrs: object) => trackBillingEvent('Save Payment Button Clicked', attrs);

export const trackBillingIntervalChange = (interval: BillingIntervals) =>
  trackBillingEvent('Billing Frequency Toggle Clicked', {
    interval,
  });

export const trackPromoCodeEvent = createTrackerForCategory('Promo Codes');
export const trackPromoCodeSuccess = (attrs: { promoCode: string }) =>
  trackPromoCodeEvent('Valid Promo Code Added', attrs);
export const trackPromoCodeFail = (attrs: { promoCode: string }) =>
  trackPromoCodeEvent('Invalid Promo Code Added', attrs);
export const trackPromoCodeApplyButtonClicked = (attrs: { promoCode: string }) =>
  trackPromoCodeEvent('Apply Promo Code Button Clicked', attrs);

const limitEventNames = {
  [LimitNames.MONTHLY_ACTIVE_USERS]: 'Client MAUs Adjusted',
  [LimitNames.EVENTS_RECEIVED]: 'Experimentation Events Adjusted',
  [LimitNames.SEATS]: 'Seats Adjusted',
  [LimitNames.EVENTS_PUBLISHED]: 'Data Export Events Adjusted',
  [LimitNames.EXPERIMENTATION_KEYS]: 'Unique Experimentation Keys Adjusted',
  [LimitNames.SSO]: 'Single Sign On Adjusted',
  [LimitNames.SERVICE_CONNECTIONS]: 'Service Connections Adjusted',
  [LimitNames.HOSTS]: 'Hosts (Service Connections) Adjusted',
  [LimitNames.EXPERIMENTATION_MAU]: 'Experimentation MAUs Adjusted',
  [LimitNames.SESSIONS]: 'Sessions Adjusted',
  [LimitNames.ERRORS]: 'Errors Adjusted',
  [LimitNames.LOGS]: 'Logs Adjusted',
  [LimitNames.TRACES]: 'Traces Adjusted',
  [LimitNames.AI_RUNS]: 'AI Runs Adjusted',
  [LimitNames.VEGA_AI]: 'Vega Prompts Adjusted',
};

export const trackLimitAdjustment = (limitName: LimitNames, quantity: number) =>
  trackBillingEvent(limitEventNames[limitName], {
    quantity,
  });

export const trackInvoiceIconClick = () => trackBillingEvent('Invoice Download Icon Clicked');

export const trackUpdateSubscriptionButtonClick = ({
  selectedSubscription, // the subscription that's being previewed for purchase
  selectedSubscriptionPrice,
  subscription, // the subscription that the account currently has.
}: {
  selectedSubscription: SubscriptionChange;
  selectedSubscriptionPrice?: SubscriptionPrice;
  subscription: Subscription;
}) => {
  let price;

  if (
    selectedSubscription?.planType === PlanTypes.FOUNDATION2023 ||
    subscription?.billingProcess === BillingProcess.USAGE_BASED_BILLING ||
    subscription?.billingProcess === BillingProcess.COMMIT_WITH_OVERAGE
  ) {
    // Don't try to get the price for usage based billing.
    price = null;
  } else if (selectedSubscription.isYearly()) {
    price = selectedSubscriptionPrice?.getYearlyPrice();
  } else {
    price = selectedSubscriptionPrice?.getMonthlyPrice();
  }

  trackBillingEvent('Update Subscription Button Clicked', {
    seatCount: selectedSubscription.getSeatCount(),
    hasSSO: selectedSubscription.hasSSO(),
    price,
    planType: selectedSubscription.planType,
    oldPlan: subscription,
  });
};

export const trackBillingLoaded = () => trackBillingEvent('Main Billing Page Loaded');
export const trackPlanConfirmationLoaded = () => trackBillingEvent('Billing Plan Confirmation Page Loaded');

export const trackTaxAddressBannerClick = () => trackBillingEvent('Tax Address Collection Banner Clicked');
export const trackTaxAddressBannerDismiss = () => trackBillingEvent('Tax Address Collection Banner Dismissed');

export const trackSSOButtonClicked = (verb: string) => trackBillingEvent(`${verb} SSO Button Clicked`);

type TrackLearnMoreOptions = {
  feature: string;
  component: string;
  learnMoreUrl: string;
};

export const trackLearnMoreClicked = ({ feature, component, learnMoreUrl }: TrackLearnMoreOptions) =>
  trackBillingEvent('Billing learn more clicked', {
    feature,
    component,
    url: window.location.href,
    path: window.location.pathname,
    learnMoreUrl,
  });

type TrackMaxPlanOptionReachedOptions = {
  component: string;
  plan: string;
  unit: string;
  planYear?: number;
};

export const trackMaxPlanOptionReached = ({ component, plan, unit, planYear }: TrackMaxPlanOptionReachedOptions) => {
  const options: TrackMaxPlanOptionReachedOptions & { url: string; path: string } = {
    component,
    plan,
    unit,
    url: window.location.href,
    path: window.location.pathname,
  };

  if (planYear) {
    options.planYear = planYear;
  }

  trackBillingEvent('Billing option limit reached', options);
};

export const ubbExperimentationKeysPricingDescription = enableSelfServePaygFreeUnits()
  ? 'First 10,000 free, then $33 per 10,000 experimentation keys, per month'
  : '$33 per 10,000 experimentation keys, per month';
export const ubbExperimentationMauPricingDescription = (freeQuantity: number, overagePriceInCents: number) =>
  enableSelfServePaygFreeUnits()
    ? `First ${freeQuantity?.toLocaleString()} free, then \$${(overagePriceInCents / 100).toFixed(2)} per 1,000 experimentation MAU, per month`
    : `\$${(overagePriceInCents / 100).toFixed(2)} per 1,000 experimentation MAU, per month`;
export const ubbEnterpriseContextPricingDescription = 'Variable pricing based on volume';
export const ubbBasePriceDescription = enableSelfServePaygFreeUnits()
  ? 'First 5 free, then $12 per service connection, per month'
  : '$12 per service connection, per month';

export const getUUBPricingDescription = (product: AnnualCommitOverageProductDetails) => {
  const freeQuantityFormatted = formatNumber(product.freeQuantity, {
    compactDisplay: 'short',
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  const pricePerBlock = product.monthlyOverageDetails.pricePerBlockCents / 100;
  const monthlyPrice = formatCurrency(pricePerBlock);

  const blockSizeFormatted = formatNumber(product.monthlyOverageDetails.blockSize, {
    compactDisplay: 'short',
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  const showAsterisk = product.hasBulkPricing === true;

  if (product.monthlyOverageDetails.blockSize === 1) {
    return enableSelfServePaygFreeUnits()
      ? `First ${freeQuantityFormatted} free, then ${monthlyPrice} per ${getLimitDisplayName(product.limitName, 'displayName')}, per month`
      : `${monthlyPrice} per ${getLimitDisplayName(product.limitName, 'displayName')}, per month`;
  }

  return enableSelfServePaygFreeUnits()
    ? `First ${freeQuantityFormatted} free, then ${monthlyPrice}/${blockSizeFormatted}${showAsterisk ? '*' : ''} ${getLimitDisplayName(product.limitName, 'plural')}, per month`
    : `${monthlyPrice}/${blockSizeFormatted}${showAsterisk ? '*' : ''} ${getLimitDisplayName(product.limitName, 'plural')}, per month`;
};

export const getSelectedBillingInterval = (
  activePlan?: Plan,
  subscription?: Subscription,
  selectedBillingInterval?: BillingIntervals | null,
): BillingIntervals => {
  const hasSelectedNewPlanType = activePlan?.type !== subscription?.planType;
  const hasPaidPlan = subscription && subscription.hasPlan() && !subscription.isTrial();

  if (selectedBillingInterval) {
    // If the user selected a billing interval manually, use it.
    return selectedBillingInterval;
  } else if (hasPaidPlan && !hasSelectedNewPlanType) {
    // Use the current paid subscription's billing interval, unless they selected
    // a different plan that they are not yet subscribed to. In that case, use the plans default.
    return subscription.billingInterval;
  }

  // Developer plans (both legacy and 2025) default to monthly
  if (activePlan?.isDeveloperTier()) {
    return BillingIntervals.MONTHLY;
  }

  // Otherwise fallback to defaults. Foundation defaults to monthly.
  const defaultFoundationPlan = subscription?.billingInterval || BillingIntervals.MONTHLY;
  return activePlan?.isFoundation() ? defaultFoundationPlan : BillingIntervals.YEARLY;
};
